import { useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '../../hooks/useQueries'

/** Asset-detail path whose response embeds the `relations` block. */
const ASSET_DETAIL_PATH = '/api/v1/organizations/{orgCode}/assets/{assetUid}'

/**
 * Invalidates everything that shows a relation after a create/delete.
 *
 * Both endpoints of an edge must refresh, not just the focal asset: creating
 * "boiler serves staircase" changes the staircase page too (spec
 * 2026-08-16-03 §7). Uses the predicate-based pattern from `front/CLAUDE.md`
 * because openapi-react-query keys are `['get', path, { params }]`, so a plain
 * `queryKey` prefix match never fires.
 */
export function useRelationInvalidation(org: string) {
    const queryClient = useQueryClient()

    return useCallback(
        (assetUids: Array<string | undefined | null>) => {
            const uids = new Set(assetUids.filter((u): u is string => !!u))
            if (uids.size === 0) return

            for (const uid of uids) {
                queryClient.invalidateQueries({ queryKey: queryKeys.asset(org, uid) })
            }

            // The org-wide relation list (the Équipements tab's networks
            // section, spec 2026-10-25-24) reads edges of a whole subtree.
            queryClient.invalidateQueries({
                predicate: (query) => {
                    const key = query.queryKey as unknown[]
                    return key[0] === 'get' && key[1] === '/api/v1/organizations/{orgCode}/asset-relations'
                },
            })

            queryClient.invalidateQueries({
                predicate: (query) => {
                    const key = query.queryKey as unknown[]
                    if (key.length < 2 || key[0] !== 'get') return false
                    if (key[1] !== ASSET_DETAIL_PATH) return false
                    const params = (
                        key.find(
                            (item) => item && typeof item === 'object' && 'params' in item,
                        ) as { params?: { path?: { orgCode?: string, assetUid?: string } } } | undefined
                    )?.params
                    if (params?.path?.orgCode !== org) return false
                    return !!params.path.assetUid && uids.has(params.path.assetUid)
                },
            })

            // Lists / trees / the graph render the relations link-count badge
            // (`link_counts.relations`), so they go stale on every edge change.
            queryClient.invalidateQueries({ queryKey: ['assets', org] })
        },
        [org, queryClient],
    )
}
